import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { MaybeRefOrGetter } from "vue";
import type { components } from "@scalty/contracts";

/**
 * Connector data layer (design §1 — Connectors & ingestion).
 *
 * The single sanctioned place the portal creates / deletes / syncs connectors
 * and browses their fetchable resources. Every call goes through `apiFetch`
 * over the generated contract; mutations own the cache-invalidation that keeps
 * the connector list, dataset list, and per-connector browses coherent.
 * Success/error *toasts* stay with the calling surface (an import, a resync, a
 * multi-select create all speak differently) — the composable owns integrity,
 * the caller owns voice.
 */

type ConnectorList = components["schemas"]["ConnectorList"];
type ConnectorEnvelope = components["schemas"]["ConnectorEnvelope"];
type ConnectorObject = components["schemas"]["ConnectorObject"];
type ConnectorObjectList = components["schemas"]["ConnectorObjectList"];
type ConnectorTable = components["schemas"]["ConnectorTable"];
type ConnectorTableList = components["schemas"]["ConnectorTableList"];
type CreateConnectorRequest = components["schemas"]["CreateConnectorRequest"];
type CreateDatasetRequest = components["schemas"]["CreateDatasetRequest"];
// The object/table pick that selects a connector's source resource: an ObjectPick
// for an object store, a TablePick for a SQL engine, and nothing at all for an
// HTTP API, whose stored configuration already determines what a sync reads.
type ConnectorPick = components["schemas"]["ConnectorSourcePick"]["pick"];
type DatasetEnvelope = components["schemas"]["DatasetEnvelope"];
type DatasetSourceEnvelope = components["schemas"]["DatasetSourceEnvelope"];
type ConnectorSourcePick = components["schemas"]["ConnectorSourcePick"];
type ConnectorType = components["schemas"]["Connector"]["type"];

// Object stores browse via GET /connectors/{id}/objects (key/size/modified);
// SQL sources via GET /connectors/{id}/tables (schema/name/~rows). Today the
// contract enum is only `s3` | `postgres`; the sets keep the routing honest and
// give a single seam when `gcs` / `mysql` join the enum (DESIGN_REQUESTS §1.1).
const OBJECT_STORE_TYPES = new Set<string>(["s3", "gcs"]);
const SQL_TYPES = new Set<string>(["postgres", "mysql"]);

export function isObjectStoreConnector(type: string): boolean {
  return OBJECT_STORE_TYPES.has(type);
}
export function isSqlConnector(type: string): boolean {
  return SQL_TYPES.has(type);
}

/**
 * Credential rotation (PUT /connectors/{id}/credentials) is LIVE — in the
 * contract and live-verified end-to-end 2026-07-14 (CONNECT-4: S3 on prod,
 * SQL against a real MySQL). The rotation dialog fires the real mutation,
 * guarded by a probe-before-commit; this flag stays as the single source of
 * truth any future rotate affordance can read.
 */
export const ROTATE_CREDENTIALS_AVAILABLE = true as const;

/** Normalized browse payload — a connector is one kind, so only one is filled. */
export interface ConnectorResources {
  objects: ConnectorObject[];
  tables: ConnectorTable[];
  truncated: boolean;
}

/**
 * The multi-select resource browse behind ConnectorResourceBrowse (frame 1n).
 * LIVE for `s3` (objects) and `postgres` (tables). `prefix` narrows an object
 * listing to one folder (contract: must lie under the connector's configured
 * prefix) and is ignored for SQL sources. An unreachable source is surfaced,
 * not retried — it stays unreachable until the customer fixes the config.
 */
export function useConnectorResourcesQuery(params: {
  connectorId: MaybeRefOrGetter<string>;
  connectorType: MaybeRefOrGetter<ConnectorType>;
  prefix: MaybeRefOrGetter<string | null>;
  enabled: MaybeRefOrGetter<boolean>;
}) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: computed(() => {
      const type = toValue(params.connectorType);
      const rawPrefix = toValue(params.prefix);
      const prefix = isObjectStoreConnector(type) ? (rawPrefix?.trim() || null) : null;
      return qk.connectorResources(toValue(params.connectorId), prefix);
    }),
    queryFn: async (): Promise<ConnectorResources> => {
      const id = toValue(params.connectorId);
      const type = toValue(params.connectorType);
      if (isSqlConnector(type)) {
        const list = await apiFetch<ConnectorTableList>(`/connectors/${id}/tables`);
        return { objects: [], tables: list.tables, truncated: list.truncated };
      }
      const rawPrefix = toValue(params.prefix)?.trim() ?? "";
      const qs = rawPrefix !== "" ? `?prefix=${encodeURIComponent(rawPrefix)}` : "";
      const list = await apiFetch<ConnectorObjectList>(`/connectors/${id}/objects${qs}`);
      return { objects: list.objects, tables: [], truncated: list.truncated };
    },
    enabled: computed(() => toValue(params.enabled)),
    retry: false,
  });

  const objects = computed<ConnectorObject[]>(() => data.value?.objects ?? []);
  const tables = computed<ConnectorTable[]>(() => data.value?.tables ?? []);
  const truncated = computed(() => data.value?.truncated ?? false);
  // The browse endpoints report an unreachable source as 502 `connector_unreachable`.
  const unreachable = computed(
    () =>
      error.value instanceof ApiError &&
      (error.value.code === "connector_unreachable" || error.value.status === 502),
  );

  return { objects, tables, truncated, isPending, error, refetch, unreachable };
}

/**
 * Connector mutations with the invalidations that keep the caches coherent.
 * Toasts live at the call site; every caller may still pass per-call
 * `onSuccess`/`onError` to `mutate` for its own voice.
 */
export function useConnectors() {
  const queryClient = useQueryClient();
  const jsonHeaders = { "Content-Type": "application/json" } as const;

  const createConnector = useMutation({
    mutationFn: (body: CreateConnectorRequest) =>
      apiFetch<ConnectorEnvelope>("/connectors", {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(body),
      }),
    onSuccess: ({ connector }) => {
      // Seed the cache so a follow-on browse appears immediately; invalidation
      // reconciles with the server in the background.
      queryClient.setQueryData<ConnectorList>(qk.connectors(connector.projectId), (old) => ({
        connectors: [connector, ...(old?.connectors ?? [])],
        nextCursor: old?.nextCursor ?? null,
      }));
      void queryClient.invalidateQueries({ queryKey: ["connectors"] });
    },
  });

  const deleteConnector = useMutation({
    // DELETE now returns 204 No Content (RU-3) — expect no body.
    mutationFn: (id: string) => apiFetch<null>(`/connectors/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["connectors"] });
      // Synced datasets keep their data but lose the link to this connector.
      void queryClient.invalidateQueries({ queryKey: ["datasets"] });
    },
  });

  // A connector fills a dataset through its own source, not through a dataset
  // creation body: the container is created typed and empty, the connector is
  // bound to it as a source, and the first sync brings the content in as
  // members. Three requests, each one the contract's own step, so the same
  // sequence serves a new dataset and a dataset that already exists.
  const attachSource = async (datasetId: string, connectorId: string, pick?: ConnectorPick) => {
    const { source } = await apiFetch<DatasetSourceEnvelope>(
      `/datasets/${encodeURIComponent(datasetId)}/sources`,
      {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ connectorId, ...(pick ? { pick } : {}) } satisfies ConnectorSourcePick),
      },
    );
    // Binding says where to read; the sync is what reads. Without it the source
    // sits idle and the dataset stays empty, which would read as a silent no-op.
    await apiFetch<null>(
      `/datasets/${encodeURIComponent(datasetId)}/sources/${encodeURIComponent(source.id)}/syncs`,
      { method: "POST" },
    );
    return source;
  };
  const refreshAfterSource = () => {
    // A sync fills a dataset and moves the connector's lastSyncedAt / lastError.
    void queryClient.invalidateQueries({ queryKey: ["datasets"] });
    void queryClient.invalidateQueries({ queryKey: ["connectors"] });
    void queryClient.invalidateQueries({ queryKey: ["studio"] });
    void queryClient.invalidateQueries({ queryKey: ["studio-source"] });
  };

  /** A new typed container filled from one connector pick. */
  const createDatasetFromConnector = useMutation({
    mutationFn: async ({
      connectorId,
      name,
      kind,
      projectId,
      pick,
    }: {
      connectorId: string;
      name: string;
      // A dataset is a typed container, so the kind is chosen before anything is
      // read; the contract requires it on every creation.
      kind: CreateDatasetRequest["kind"];
      projectId?: string;
      pick?: ConnectorPick;
    }) => {
      const { dataset } = await apiFetch<DatasetEnvelope>("/datasets", {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ name, kind, ...(projectId ? { projectId } : {}) } satisfies CreateDatasetRequest),
      });
      await attachSource(dataset.id, connectorId, pick);
      return dataset;
    },
    onSuccess: refreshAfterSource,
  });

  /** The same binding for a dataset that already exists — the dataset page's Attach source. */
  const addSourceToDataset = useMutation({
    mutationFn: ({ datasetId, connectorId, pick }: { datasetId: string; connectorId: string; pick?: ConnectorPick }) =>
      attachSource(datasetId, connectorId, pick),
    onSuccess: refreshAfterSource,
  });

  /* Credential rotation goes through the Rotate credentials dialog (PUT /connectors/{id}/credentials in ActionHost). */
  return { createConnector, deleteConnector, createDatasetFromConnector, addSourceToDataset };
}
